import { useMemo, useState } from "react";
import { Copy } from "lucide-react";

import type { FactSheet } from "@/api/schemas";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { parseNumericTokens, type NumericMatch } from "./parseNumericTokens";

/**
 * Sprint B — W4.
 *
 * Renders a section's prose as a span tree where every fact-matched
 * numeric token is wrapped in a shadcn Tooltip. Tooltip body shows:
 *
 * * Fact key + raw value from the fact sheet.
 * * Source stamp (template / IFC / placeholder) from
 *   ``fact_sheet._sources``.
 * * IFC GlobalId list (``fact_sheet._sources_globalids``) — copy-able,
 *   so the architect can paste the handle into Solibri / BIMcollab to
 *   inspect the source entity.
 *
 * Pure presentational; no hooks beyond ``useMemo`` so it's safe to
 * mount inside the regen-mutation suspended state.
 */

interface NumericChipsProps {
  content: string;
  factSheet: FactSheet;
}

export function NumericChips({ content, factSheet }: NumericChipsProps) {
  // Single memo so hooks aren't conditional (rules-of-hooks): empty
  // ``matches`` simply yield ``[content]``. Tooltip swap lives in the
  // Radix portal, not in this tree, so hover doesn't invalidate.
  const nodes = useMemo(() => {
    const matches = parseNumericTokens(
      content,
      factSheet as Record<string, unknown>,
    );
    if (matches.length === 0) return [content];
    return buildSpanTree(content, matches, factSheet);
  }, [content, factSheet]);

  return <>{nodes}</>;
}

function buildSpanTree(
  content: string,
  matches: NumericMatch[],
  factSheet: FactSheet,
): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let cursor = 0;
  matches.forEach((m, i) => {
    if (m.startIdx > cursor) {
      out.push(content.slice(cursor, m.startIdx));
    }
    out.push(
      <NumericChip key={`chip-${i}-${m.startIdx}`} match={m} factSheet={factSheet} />,
    );
    cursor = m.endIdx;
  });
  if (cursor < content.length) {
    out.push(content.slice(cursor));
  }
  return out;
}

interface NumericChipProps {
  match: NumericMatch;
  factSheet: FactSheet;
}

function NumericChip({ match, factSheet }: NumericChipProps) {
  const sources =
    (factSheet._sources as Record<string, string> | undefined) ?? {};
  const globalIds =
    (factSheet._sources_globalids as Record<string, string[]> | undefined) ??
    {};

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className={cn(
            "cursor-help rounded-sm border-b border-dotted border-ind-400 px-0.5 transition-colors hover:bg-ind-50 focus:bg-ind-50 focus:outline-none",
          )}
          aria-label={`Numerička vrijednost ${match.text}, izvor: ${match.candidateFactKeys[0]}`}
        >
          {match.text}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        className="max-w-[380px] space-y-2 p-3"
      >
        <div className="text-[0.78rem] font-medium text-white">
          Vrijednost: {match.text}
        </div>
        <div className="space-y-1.5">
          {match.candidateFactKeys.map((key) => {
            const factValue = lookupFactValue(factSheet, key);
            const source = sources[key];
            const handles = globalIds[key] ?? [];
            return (
              <div
                key={key}
                className="rounded bg-white/10 px-2 py-1.5 text-[0.74rem]"
              >
                <div className="font-mono text-white">{key}</div>
                {factValue !== undefined && (
                  <div className="text-white/70">
                    fact_sheet: <span className="text-white">{factValue}</span>
                  </div>
                )}
                {source && (
                  <div className="mt-1 inline-flex items-center gap-1 rounded bg-white/15 px-1.5 py-0.5 text-[0.68rem] uppercase tracking-wide text-white">
                    izvor: {source}
                  </div>
                )}
                {handles.length > 0 && (
                  <GlobalIdList handles={handles} />
                )}
              </div>
            );
          })}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function lookupFactValue(
  factSheet: FactSheet,
  factKey: string,
): string | undefined {
  // Walk the fact sheet looking for the first leaf whose key segment
  // equals ``factKey``. Mirrors :func:`parseNumericTokens.collectFactLeaves`.
  let result: number | undefined;
  function walk(node: unknown, lastKey: string): void {
    if (result !== undefined) return;
    if (typeof node === "number" && Number.isFinite(node)) {
      if (lastKey === factKey) result = node;
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((v) => walk(v, lastKey));
      return;
    }
    if (node && typeof node === "object") {
      for (const [k, v] of Object.entries(node)) {
        if (k.startsWith("_")) continue;
        walk(v, k);
      }
    }
  }
  walk(factSheet, "");
  if (result === undefined) return undefined;
  return Number.isInteger(result) ? `${result}` : result.toFixed(2);
}

function GlobalIdList({ handles }: { handles: string[] }) {
  const [copied, setCopied] = useState<string | null>(null);
  const display = handles.slice(0, 5);
  const remainder = handles.length - display.length;

  async function copy(handle: string) {
    try {
      await navigator.clipboard.writeText(handle);
      setCopied(handle);
      window.setTimeout(() => setCopied(null), 1200);
    } catch {
      // Clipboard may be unavailable in insecure contexts; fail silently.
    }
  }

  return (
    <div className="mt-1.5 space-y-0.5">
      <div className="text-[0.68rem] uppercase tracking-wide text-white/60">
        IFC GlobalId
      </div>
      {display.map((h) => (
        <button
          key={h}
          type="button"
          onClick={() => copy(h)}
          className="flex w-full items-center gap-1.5 rounded bg-white/5 px-1.5 py-0.5 text-left font-mono text-[0.7rem] text-white/85 transition-colors hover:bg-white/15"
          title="Klikni za kopiranje"
        >
          <span className="flex-1 truncate">{h}</span>
          <Copy className="h-3 w-3 shrink-0 opacity-60" aria-hidden />
          {copied === h && (
            <span className="text-[0.65rem] text-emerald-300">kopirano</span>
          )}
        </button>
      ))}
      {remainder > 0 && (
        <div className="text-[0.65rem] text-white/50">
          +{remainder} dodatn{remainder === 1 ? "i" : "ih"}
        </div>
      )}
    </div>
  );
}
